/**
 * BankSubmissionStatus — the v1 hero page.
 *
 * Layout:
 *   Page head · As-of timestamp + manual refresh
 *   Summary tiles (Green / Amber / Red / Grey counts)
 *   FilterBar (search + flag multiselect + auto-refresh toggle)
 *   Sortable table of every bank
 *   Drill-in drawer (opens on row click; last 30 days of tblDataLoad)
 *
 * Data lifecycle:
 *   - On mount: dispatch fetchSubmissionStatus.
 *   - On the auto-refresh toggle being on: re-fetch every 60s.
 *   - On manual refresh button: re-fetch immediately.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

import Card from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import Ico from '../../components/Ico';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import { formatDateTime } from '../../helpers/dateFormat';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
	fetchSubmissionStatus,
	selectSubmissionAsOf,
	selectSubmissionStatus,
	selectSubmissionStatusError,
	selectSubmissionStatusState,
} from '../../redux/slices/banks';
import { selectAutoRefresh, setAutoRefresh } from '../../redux/slices/ui';
import type { BankSubmissionRow, Flag } from '../../type/api';

import BankLoadsDrawer from './BankLoadsDrawer';
import FilterBar from './FilterBar';
import SubmissionTable from './SubmissionTable';
import SummaryTiles from './SummaryTiles';

const ALL_FLAGS: Flag[] = ['GREEN', 'AMBER', 'RED', 'GREY'];
const REFRESH_INTERVAL_MS = 60_000;

export default function BankSubmissionStatusPage() {
	const dispatch = useAppDispatch();
	const rows = useAppSelector(selectSubmissionStatus);
	const asOf = useAppSelector(selectSubmissionAsOf);
	const status = useAppSelector(selectSubmissionStatusState);
	const error = useAppSelector(selectSubmissionStatusError);
	const autoRefresh = useAppSelector(selectAutoRefresh);

	const [search, setSearch] = useState('');
	const [enabledFlags, setEnabledFlags] = useState<Set<Flag>>(new Set(ALL_FLAGS));
	const [drawerBank, setDrawerBank] = useState<BankSubmissionRow | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		dispatch(fetchSubmissionStatus());
	}, [dispatch]);

	useEffect(() => {
		if (!autoRefresh) {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			return;
		}
		intervalRef.current = setInterval(() => {
			dispatch(fetchSubmissionStatus());
		}, REFRESH_INTERVAL_MS);
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [autoRefresh, dispatch]);

	const safeRows: BankSubmissionRow[] = Array.isArray(rows) ? rows : [];

	const filteredRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		return safeRows.filter((r) => {
			if (!enabledFlags.has(r.overallFlag)) return false;
			if (!q) return true;
			if (String(r.orgId).includes(q)) return true;
			if (r.orgName.toLowerCase().includes(q)) return true;
			return false;
		});
	}, [safeRows, search, enabledFlags]);

	const toggleFlag = (f: Flag) => {
		setEnabledFlags((prev) => {
			const next = new Set(prev);
			if (next.has(f)) {
				next.delete(f);
				if (next.size === 0) {
					// Always keep at least one selected so the table doesn't go blank.
					next.add(f);
				}
			} else {
				next.add(f);
			}
			return next;
		});
	};

	const handleRefresh = () => dispatch(fetchSubmissionStatus());
	const isLoading = status === 'loading';
	const isError = status === 'error';
	const isEmpty = status === 'success' && safeRows.length === 0;

	return (
		<div className="page-body">
			<div className="page-head">
				<div>
					<h1 className="page-title">Bank Submission Status</h1>
					<div className="page-sub">
						Per-bank LOAN / DEPOSIT / KYC submission health across all 125 UCBs.
						{asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDateTime(asOf)}
								</span>
							</>
						)}
					</div>
				</div>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<button
						type="button"
						className="btn ghost"
						onClick={handleRefresh}
						disabled={isLoading}
						title="Refresh now"
					>
						<Ico name="refresh" size={14} /> Refresh
					</button>
				</div>
			</div>

			<SummaryTiles rows={safeRows} />

			<FilterBar
				search={search}
				onSearchChange={setSearch}
				enabledFlags={enabledFlags}
				onToggleFlag={toggleFlag}
				autoRefresh={autoRefresh}
				onToggleAutoRefresh={() => dispatch(setAutoRefresh(!autoRefresh))}
			/>

			{isError && (
				<ErrorBanner
					message={error || 'Failed to load submission status'}
					onRetry={handleRefresh}
				/>
			)}

			{isLoading && safeRows.length === 0 && (
				<Card>
					<LoadingSkeleton rows={8} columns={8} />
				</Card>
			)}

			{!isLoading && isEmpty && (
				<Card>
					<EmptyState
						title="No banks loaded yet"
						hint="The ETL hasn't pushed any submissions to tblDataLoad. Run the ETL once and reload."
					/>
				</Card>
			)}

			{!isError && safeRows.length > 0 && (
				<SubmissionTable rows={filteredRows} onRowClick={setDrawerBank} />
			)}

			<BankLoadsDrawer bank={drawerBank} onClose={() => setDrawerBank(null)} />
		</div>
	);
}
